import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, Provider } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PageLoadProgressModule } from '@lib/page-load-progress';
import { NotificationsModule } from '@lib/notifications';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AsideOutletModule, LayoutModule, SocialAuthorizationModule } from '@modules';
import { environment } from '@env';
import { ReactiveFormsModule } from '@angular/forms';
import { initLocale } from '@project/translate';
import {
  GlobalErrorHandlerService,
  HttpAuthInterceptorService,
  HttpClientIdInterceptorService,
  HttpClientInterceptorService,
  HttpNgswBypassInterceptorService,
  HttpTimezoneInterceptorService,
  HttpInfoDeviceInterceptorService,
  HcService,
  DoctorSpecializationRegistryService,
} from '@project/services';
import { ModalModule } from '@lib/modal';
import { AuthorizedUserFeaturesContainerModule } from '@features/container';
import { UserInformationCenterManagerService } from './lazy-modules/user-information-center/user-information-center-manager.service';
import { SessionsPatientManagerService } from './lazy-modules/sessions/sessions-patient/sessions-patient-manager.service';
import { HttpPlatformInterceptorService } from './project/services/interceptors/http-platform-interceptor.service';
import { HttpVersionInterceptorService } from './project/services/interceptors/http-version-interceptor.service';
import { LocaleService } from './project/translate/locale-getter';
import { Router } from '@angular/router';
import { TraceService } from '@sentry/angular';
import { HttpSentryInterceptorService } from './project/services/interceptors/http-sentry-interceptor.service';

const INTERCEPTORS_PROVIDERS: Provider[] = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpAuthInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpClientInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpTimezoneInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpNgswBypassInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpClientIdInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpInfoDeviceInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpPlatformInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpVersionInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpSentryInterceptorService,
    multi: true,
  },
];

export function getLocaleFactory(localeService: LocaleService): string {
  return localeService.getCurrentLocale();
}

export function initialiseHcRemoteConfigFactory(hcService: HcService): () => Promise<void> {
  return () => hcService.initialiseRemoteConfig();
}

export function initialiseDoctorSpecializationsFactory(
  doctorSpecializationRegistryService: DoctorSpecializationRegistryService,
): () => Promise<void> {
  return () => doctorSpecializationRegistryService.initialise();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserAnimationsModule,
    HttpClientModule,
    PageLoadProgressModule,
    NotificationsModule,
    AppRoutingModule,
    LayoutModule,
    ServiceWorkerModule.register('combined-sw.js', { enabled: !environment.serveMode }),
    SocialAuthorizationModule.forRoot(),
    ReactiveFormsModule, // For modal overlays forms
    AsideOutletModule,
    ModalModule,
    AuthorizedUserFeaturesContainerModule,
  ],
  providers: [
    INTERCEPTORS_PROVIDERS,
    {
      /**
       * For usage in CookieService.
       */
      provide: 'req',
      useValue: null,
    },
    {
      provide: ErrorHandler,
      useExisting: GlobalErrorHandlerService,
    },
    {
      /* Registers Sentry's tracing service to monitor Angular route changes */
      provide: TraceService,
      deps: [Router],
    },
    {
      /* TraceService instantiate on app startup to begin capturing performance data immediately */
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialiseHcRemoteConfigFactory,
      deps: [HcService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialiseDoctorSpecializationsFactory,
      deps: [DoctorSpecializationRegistryService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: getLocaleFactory,
      deps: [LocaleService],
    },
    UserInformationCenterManagerService,
    SessionsPatientManagerService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private localeService: LocaleService) {
    initLocale(this.localeService);
  }
}
